import { Modal, Form, Input, Button, Upload, message, Steps, Alert, Typography, Select, DatePicker, InputNumber } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, BankOutlined, UploadOutlined, QrcodeOutlined, LockOutlined, CalendarOutlined, DollarOutlined, EditOutlined } from '@ant-design/icons';
import {useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import { RegisterOrg, CheckOrgInfo, getAreaInfo, uploadFile, getCategoryInfo, getDepartTreeList, updateOrgInfo } from '../../server/api';
import WangEditor from '../WangEditor';
import s from './index.module.css';

const { TextArea } = Input;
const { Step } = Steps;
const { Text, Paragraph } = Typography;

const BASIC_INFO_FIELDS = ['name', 'short_name', 'legalPerson', 'category', 'orgLabel', 'telephone', 'orgEmail', 'creditCode', 'checkUrl', 'province', 'city', 'county', 'regAddress', 'officeAddress', 'busScope', 'checkDesc', 'regTime', 'regAmount', 'orgProfile'];
const CERT_FIELDS = ['businessCert', 'checkAbility', 'qualificationCert', 'manageOrgId', 'checkLogo', 'orgContact', 'contactTel'];
const PASSWORD_FIELDS = ['regPassword', 'confirmPassword'];

// 密码强度计算：长度 + 字符多样性，返回 1~3 档
const getPasswordStrength = (value = '') => {
  if (!value) return { level: 0, label: '' };
  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  if (score <= 1) return { level: 1, label: '弱' };
  if (score <= 3) return { level: 2, label: '中' };
  return { level: 3, label: '强' };
};

const getFileNameFromUrl = (url = '') => {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    return decodeURIComponent(pathname.split('/').pop() || '附件');
  } catch (_) {
    return url.split('/').pop() || '附件';
  }
};

const normalizeUploadFileList = (value, prefix) => {
  if (!value) return [];

  const toFileItem = (item, index) => {
    if (!item) return null;
    if (item.uid && item.name) return item;

    if (typeof item === 'string') {
      const isUrl = /^https?:\/\//.test(item) || item.startsWith('/');
      return {
        uid: `${prefix}-${index}`,
        name: isUrl ? getFileNameFromUrl(item) : `已上传附件${index + 1}`,
        status: 'done',
        ...(isUrl ? { url: item } : { fileId: item }),
      };
    }

    const url = item.url || item.fileUrl || item.path;
    const fileId = item.fileId || item.id || item.value;
    const name = item.name || item.fileName || (url ? getFileNameFromUrl(url) : `附件${index + 1}`);

    return {
      uid: item.uid || `${prefix}-${index}`,
      name,
      status: item.status || 'done',
      ...(url ? { url } : {}),
      ...(fileId ? { fileId } : {}),
    };
  };

  const list = Array.isArray(value) ? value : [value];
  return list.map(toFileItem).filter(Boolean);
};

const extractUploadValue = (fileList, multi = false) => {
  const normalized = (fileList || []).map((file) => file?.fileId || file?.url || file?.name).filter(Boolean);
  return multi ? normalized : normalized[0] || '';
};

const getUploadResultId = (response) => {
  const result = response?.data?.result ?? response?.result ?? response;
  if (!result) return '';
  if (typeof result === 'string') return result;
  return result.id || result.fileId || result.value || '';
};

const normalizeUploadEvent = (e) => {
  const fileList = Array.isArray(e) ? e : e?.fileList || [];
  return fileList.map((file) => {
    const responseId = getUploadResultId(file.response);
    if (!responseId) return file;

    return {
      ...file,
      status: 'done',
      fileId: responseId,
    };
  });
};

const hasPendingUpload = (fileList = [], required = false) => {
  if (!fileList.length) return required;
  return fileList.some((file) => file?.status === 'uploading' || file?.status === 'error' || (!file?.fileId && !file?.url));
};

const normalizeAreaOptions = (list = []) => (
  list.map((item) => ({
    label: item.name,
    value: item.id,
    fid: item.fid,
  }))
);

// 组织机构树 → 平铺下拉 options，层级用前缀缩进表示
const flattenDepartOptions = (list = [], depth = 0, result = []) => {
  (Array.isArray(list) ? list : []).forEach((item) => {
    if (!item) return;
    const value = item.id ?? item.key ?? item.value;
    const title = item.title || item.departName || item.name || '';
    if (value) {
      result.push({
        label: `${'　'.repeat(depth)}${title}`,
        value,
      });
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      flattenDepartOptions(item.children, depth + 1, result);
    }
  });
  return result;
};

const findAreaOption = (options = [], rawValue) => {
  if (!rawValue) return null;
  const normalizedValue = String(rawValue).trim();
  return options.find((item) => (
    String(item.value) === normalizedValue ||
    item.label === normalizedValue ||
    item.label.endsWith(normalizedValue)
  )) || null;
};

const getOrganizationInitialValues = (data = {}) => ({
  id: data.id,
  fkMainId: data.fkMainId,
  name: data.name || data.regName || '',
  short_name: data.short_name || data.shortName || '',
  creditCode: data.creditCode || data.regCode || '',
  legalPerson: data.legalPerson || '',
  category: data.category || '',
  orgLabel: typeof data.orgLabel === 'string'
    ? (data.orgLabel ? data.orgLabel.split('|') : [])
    : (data.orgLabel || []),
  orgContact: data.orgContact || data.contactName || '',
  contactTel: data.contactTel || data.phoneNo || '',
  telephone: data.telephone || '',
  orgEmail: data.orgEmail || data.email || '',
  province: data.province || '',
  city: data.city || '',
  county: data.county || '',
  regAddress: data.regAddress || '',
  officeAddress: data.officeAddress || '',
  regTime: data.regTime ? moment(data.regTime) : undefined,
  regAmount: data.regAmount || '',
  busScope: data.busScope || data.natBusiness || '',
  checkDesc: data.checkDesc || '',
  orgProfile: data.orgProfile || '',
  checkUrl: data.checkUrl || '',
  businessCert: normalizeUploadFileList(data.businessCertUrl || data.businessCert, 'business-cert'),
  checkAbility: normalizeUploadFileList(data.checkAbilityUrl || data.checkAbility, 'check-ability'),
  qualificationCert: normalizeUploadFileList(data.orgCertList || data.qualificationCert, 'qualification-cert'),
  checkLogo: normalizeUploadFileList(data.checkLogoUrl || data.checkLogo, 'company-logo'),
  manageOrgId: data.manageOrgId || '',
  regPassword: '',
  confirmPassword: '',
});

const RegisterModal = ({ visible, onCancel, onRegisterSuccess, onSwitchToLogin, mode = 'register', initialOrgData = null }) => {
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('personal');
  const [currentStep, setCurrentStep] = useState(0);
  const [creditCodeChecking, setCreditCodeChecking] = useState(false);
  const [step1Values, setStep1Values] = useState({});
  const [step2Values, setStep2Values] = useState({});
  const [regPasswordValue, setRegPasswordValue] = useState('');
  const regPasswordStrength = getPasswordStrength(regPasswordValue);
  const [provinceLoading, setProvinceLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [countyLoading, setCountyLoading] = useState(false);
  const [richTextModal, setRichTextModal] = useState({ visible: false, field: '', content: '' });
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [countyOptions, setCountyOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [orgLabelOptions, setOrgLabelOptions] = useState([]);
  const [orgLabelLoading, setOrgLabelLoading] = useState(false);
  const [departOptions, setDepartOptions] = useState([]);
  const [departLoading, setDepartLoading] = useState(false);
  const [form] = Form.useForm();
  const isSupplementMode = mode === 'supplement';
  const organizationInitialValues = useMemo(
    () => getOrganizationInitialValues(initialOrgData || {}),
    [initialOrgData]
  );
  // 使用稳定引用跟踪是否已初始化过，避免 initialOrgData 引用变化导致步骤重置
  const [hasInitialized, setHasInitialized] = useState(false);
  const selectedProvince = Form.useWatch('province', form);
  const selectedCity = Form.useWatch('city', form);
  const selectedCategory = Form.useWatch('category', form);

  useEffect(() => {
    if (visible) {
      setUserType(typeof visible === 'string' ? visible : 'organization');
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (hasInitialized) return;

    const initialStep1 = BASIC_INFO_FIELDS.reduce((acc, field) => {
      acc[field] = organizationInitialValues[field];
      return acc;
    }, {});
    const initialStep2 = CERT_FIELDS.reduce((acc, field) => {
      acc[field] = organizationInitialValues[field];
      return acc;
    }, {});

    setStep1Values(initialStep1);
    setStep2Values(initialStep2);
    // 补充材料模式：如果初始数据已有第二步资料，则从第二步开始
    if (isSupplementMode && initialStep2.businessCert && initialStep2.businessCert.length > 0) {
      setCurrentStep(1);
    } else {
      setCurrentStep(0);
    }
    setHasInitialized(true);
  }, [visible, userType, organizationInitialValues, hasInitialized]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;

    if (currentStep === 0) {
      form.setFieldsValue(step1Values);
    } else if (currentStep === 1) {
      form.setFieldsValue(step2Values);
    } else {
      form.setFieldsValue(PASSWORD_FIELDS.reduce((acc, field) => {
        acc[field] = organizationInitialValues[field];
        return acc;
      }, {}));
    }
  }, [visible, userType, currentStep, form, step1Values, step2Values, organizationInitialValues]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (provinceOptions.length > 0) return;

    const loadProvinceOptions = async () => {
      setProvinceLoading(true);
      try {
        const provinceRes = await getAreaInfo({ level: 1 });
        setProvinceOptions(normalizeAreaOptions(provinceRes.data?.result));
      } catch (_) {
        message.error('地区数据加载失败，请稍后重试');
      } finally {
        setProvinceLoading(false);
      }
    };

    loadProvinceOptions();
  }, [visible, userType, provinceOptions.length]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (categoryOptions.length > 0) return;

    const loadCategoryOptions = async () => {
      setCategoryLoading(true);
      try {
        const res = await getCategoryInfo({ code: 'B03' });
        const result = res.data?.result;
        if (Array.isArray(result)) {
          const allowedCodes = ['B03A01', 'B03A03', 'B03A04', 'B03A05', 'B03A06'];
          setCategoryOptions(
            result
              .filter((item) => allowedCodes.includes(item.code))
              .map((item) => ({
                label: item.title,
                value: item.code,
              }))
          );
        }
      } catch (_) {
        message.error('服务大类数据加载失败，请稍后重试');
      } finally {
        setCategoryLoading(false);
      }
    };

    loadCategoryOptions();
  }, [visible, userType, categoryOptions.length]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (!selectedCategory) {
      setOrgLabelOptions([]);
      return;
    }

    let cancelled = false;
    const loadOrgLabelOptions = async () => {
      setOrgLabelLoading(true);
      try {
        const res = await getCategoryInfo({ code: selectedCategory });
        if (cancelled) return;
        const result = res.data?.result;
        if (Array.isArray(result)) {
          setOrgLabelOptions(result.map((item) => ({
            label: item.title,
            value: item.code,
          })));
        }
      } catch (_) {
        if (!cancelled) message.error('专业类别数据加载失败，请稍后重试');
      } finally {
        if (!cancelled) setOrgLabelLoading(false);
      }
    };

    loadOrgLabelOptions();
    return () => { cancelled = true; };
  }, [visible, userType, selectedCategory]);

  // 所属管理机关（组织机构树）
  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (departOptions.length > 0) return;

    const loadDepartOptions = async () => {
      setDepartLoading(true);
      try {
        const res = await getDepartTreeList();
        setDepartOptions(flattenDepartOptions(res.data?.result));
      } catch (_) {
        message.error('管理机关数据加载失败，请稍后重试');
      } finally {
        setDepartLoading(false);
      }
    };

    loadDepartOptions();
  }, [visible, userType, departOptions.length]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (!provinceOptions.length) return;

    setStep1Values((prev) => {
      const provinceOption = findAreaOption(provinceOptions, prev.province);
      const nextProvince = provinceOption?.value || '';

      if (prev.province === nextProvince) {
        return prev;
      }

      return {
        ...prev,
        province: nextProvince,
      };
    });
  }, [visible, userType, provinceOptions]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;

    if (!selectedProvince) {
      setCityOptions([]);
      setCountyOptions([]);
      return;
    }

    const loadCityOptions = async () => {
      setCityLoading(true);
      try {
        const cityRes = await getAreaInfo({ level: 2, fId: selectedProvince });
        const nextCityOptions = normalizeAreaOptions(cityRes.data?.result)
          .filter((item) => item.fid === selectedProvince);
        setCityOptions(nextCityOptions);
      } catch (_) {
        setCityOptions([]);
        message.error('城市数据加载失败，请稍后重试');
      } finally {
        setCityLoading(false);
      }
    };

    loadCityOptions();
  }, [visible, userType, selectedProvince]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (!cityOptions.length) return;

    setStep1Values((prev) => {
      const cityOption = findAreaOption(cityOptions, prev.city);
      const nextCity = cityOption?.value || '';

      if (prev.city === nextCity) {
        return prev;
      }

      return {
        ...prev,
        city: nextCity,
      };
    });
  }, [visible, userType, cityOptions]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;

    if (!selectedCity) {
      setCountyOptions([]);
      return;
    }

    const loadCountyOptions = async () => {
      setCountyLoading(true);
      try {
        const countyRes = await getAreaInfo({ level: 3, fId: selectedCity });
        const nextCountyOptions = normalizeAreaOptions(countyRes.data?.result)
          .filter((item) => item.fid === selectedCity);
        setCountyOptions(nextCountyOptions);
      } catch (_) {
        setCountyOptions([]);
        message.error('区县数据加载失败，请稍后重试');
      } finally {
        setCountyLoading(false);
      }
    };

    loadCountyOptions();
  }, [visible, userType, selectedCity]);

  useEffect(() => {
    if (!visible || userType !== 'organization') return;
    if (!countyOptions.length) return;

    setStep1Values((prev) => {
      const countyOption = findAreaOption(countyOptions, prev.county);
      const nextCounty = countyOption?.value || '';

      if (prev.county === nextCounty) {
        return prev;
      }

      return {
        ...prev,
        county: nextCounty,
      };
    });
  }, [visible, userType, countyOptions]);

  const changeUserType = (nextType) => {
    if (isSupplementMode) return;
    setUserType(nextType);
    setCurrentStep(0);
    setStep1Values({});
    setStep2Values({});
    form.resetFields();
  };

  const handleClose = () => {
    onCancel();
    form.resetFields();
    setCurrentStep(0);
    setUserType('personal');
    setStep1Values({});
    setStep2Values({});
    setRegPasswordValue('');
    setHasInitialized(false);
  };

  const handleProvinceChange = () => {
    setCityOptions([]);
    setCountyOptions([]);
    form.setFieldsValue({
      city: undefined,
      county: undefined,
    });
  };

  const handleCityChange = () => {
    setCountyOptions([]);
    form.setFieldsValue({
      county: undefined,
    });
  };

  const handleUploadRequest = async ({ file, onSuccess, onError }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadFile(formData);

      if (res.data?.success === false) {
        throw new Error(res.data?.message || '文件上传失败');
      }

      const fileId = getUploadResultId(res);
      if (!fileId) {
        throw new Error('文件上传成功但未返回文件ID');
      }

      onSuccess({
        ...res.data,
        result: {
          ...(typeof res.data?.result === 'object' && res.data?.result ? res.data.result : {}),
          fileId,
        },
      }, file);
    } catch (error) {
      onError(error);
      message.error(error.message || '文件上传失败，请重试');
    }
  };

  // 统一社会信用代码失焦时检查是否重复
  const handleCreditCodeBlur = async (e) => {
    const creditCode = e.target.value?.trim();
    if (!creditCode) return;
    if (organizationInitialValues.creditCode && organizationInitialValues.creditCode === creditCode) return;
    setCreditCodeChecking(true);
    try {
      const formData = new FormData();
      formData.append('creditCode', creditCode);
      const res = await CheckOrgInfo(formData);
      if (res.data && !res.data.success) {
        form.setFields([{
          name: 'creditCode',
          errors: [res.data.message || '该统一社会信用代码已被注册'],
        }]);
      }
    } catch (_) {
      // 网络异常不阻塞用户操作
    } finally {
      setCreditCodeChecking(false);
    }
  };

  // 提交机构注册申请（values 为第三步字段，第一步和第二步字段从 step1Values 和 step2Values 取）
  const handleRegister = async (values) => {
    const allValues = { ...step1Values, ...step2Values, ...values };
    if (
      hasPendingUpload(allValues.businessCert, true) ||
      hasPendingUpload(allValues.checkAbility) ||
      hasPendingUpload(allValues.qualificationCert) ||
      hasPendingUpload(allValues.checkLogo)
    ) {
      message.warning('请等待文件上传完成后再提交');
      return;
    }

    setLoading(true);
    // 存在 id（带初始数据，编辑场景）时走编辑接口，否则走注册接口
    const isEdit = !!(organizationInitialValues.id);
    try {
      const orgData = {
        id: organizationInitialValues.id,
        fkMainId: organizationInitialValues.fkMainId,
        name: allValues.name,
        shortName: allValues.short_name,
        creditCode: allValues.creditCode,
        legalPerson: allValues.legalPerson,
        category: allValues.category,
        orgLabel: Array.isArray(allValues.orgLabel) ? allValues.orgLabel.join('|') : (allValues.orgLabel || ''),
        orgContact: allValues.orgContact,
        contactTel: allValues.contactTel,
        telephone: allValues.telephone,
        orgEmail: allValues.orgEmail,
        province: allValues.province,
        city: allValues.city,
        county: allValues.county,
        regAddress: allValues.regAddress,
        officeAddress: allValues.officeAddress,
        regTime: allValues.regTime ? allValues.regTime.format('YYYY-MM-DD') : '',
        regAmount: allValues.regAmount,
        busScope: allValues.busScope,
        checkDesc: allValues.checkDesc,
        orgProfile: allValues.orgProfile,
        checkUrl: allValues.checkUrl,
        regPassword: isSupplementMode ? undefined : allValues.regPassword,
        businessCert: extractUploadValue(allValues.businessCert),
        checkAbility: extractUploadValue(allValues.checkAbility),
        orgCertList: extractUploadValue(allValues.qualificationCert, true),
        checkLogo: extractUploadValue(allValues.checkLogo),
        manageOrgId: allValues.manageOrgId,
        state: isSupplementMode ? 1 : 3,
      };
      const res = isEdit ? await updateOrgInfo(orgData) : await RegisterOrg(orgData);
      if (res.data?.success) {
        message.success(isEdit ? '编辑申请已提交，请等待审核' : (isSupplementMode ? '补充材料已提交，请等待审核' : '机构注册申请已提交，审核通过后将发送账号密码至联系邮箱'));
        onRegisterSuccess?.(orgData);
        handleClose();
      } else {
        message.error(res.data?.message || (isEdit ? '编辑申请提交失败，请重试' : (isSupplementMode ? '补充材料提交失败，请重试' : '注册失败，请重试')));
      }
    } catch (_) {
      message.error(isEdit ? '编辑申请提交失败，请重试' : (isSupplementMode ? '补充材料提交失败，请重试' : '注册失败，请重试'));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = async () => {
    try {
      await form.validateFields();
      if (currentStep === 0) {
        setStep1Values((prev) => ({ ...prev, ...form.getFieldsValue(BASIC_INFO_FIELDS) }));
        setCurrentStep(1);
      } else if (currentStep === 1) {
        const certValues = form.getFieldsValue(CERT_FIELDS);
        if (
          hasPendingUpload(certValues.businessCert, true) ||
          hasPendingUpload(certValues.checkAbility) ||
          hasPendingUpload(certValues.qualificationCert) ||
          hasPendingUpload(certValues.checkLogo)
        ) {
          message.warning('请等待文件上传完成后再继续');
          return;
        }

        setStep2Values((prev) => ({ ...prev, ...certValues }));
        if (!isSupplementMode) {
          setCurrentStep(2);
        }
      }
    } catch (_) {
      // 验证未通过，不跳转
    }
  };

  // 普通用户：展示小程序二维码，引导注册后扫码登录
  const renderEnterpriseGuide = () => (
    <div className={s.enterpriseGuide}>
      <div className={s.qrcodeWrap}>
        <img src="/xcx.jpg" alt="小程序二维码" className={s.qrcodeImg} />
        <p className={s.qrcodeHint}>微信扫码 或 搜索小程序完成注册</p>
      </div>
      <div className={s.guideSteps}>
        <Paragraph className={s.guideStep}><Text strong className={s.guideStep}>①</Text> 微信扫描上方二维码，或搜索「庆质通」小程序</Paragraph>
        <Paragraph className={s.guideStep}><Text strong className={s.guideStep}>②</Text> 在小程序内完成账号注册</Paragraph>
        <Paragraph className={s.guideStep}><Text strong className={s.guideStep}>③</Text> 注册成功后，账号和密码将发送到您的消息中</Paragraph>
        <Paragraph className={s.guideStep}><Text strong className={s.guideStep}>④</Text> 返回本平台，点击右上角"登录" → 选择"个人登录"</Paragraph>
        <Paragraph className={s.guideStep}><Text strong className={s.guideStep}>⑤</Text> 使用小程序扫描二维码/输入账密完成登录</Paragraph>
      </div>
      <div className={s.guideActions}>
        <Button
          type="primary"
          icon={<QrcodeOutlined />}
          size="large"
          block
          onClick={() => { handleClose(); onSwitchToLogin?.(); }}
        >
          已注册，去扫码登录
        </Button>
      </div>
    </div>
  );

  // 第一步：机构基本信息（字段与 registerOrg 接口 schema 对应）
  const renderBasicInfoForm = () => (
    <div className={`${s.registerFormSection} ${s.registerFormGrid}`}>
      <Form.Item name="name" label="企业名称" rules={[{ required: true, message: '请输入企业名称' }]}>
        <Input placeholder="请输入企业名称" />
      </Form.Item>
      <Form.Item
          name="creditCode"
          label="社会信用代码"
          rules={[
            { required: true, message: '请输入统一社会信用代码' },
            { len: 18, message: '统一社会信用代码为18位' },
          ]}
      >
        <Input
            placeholder="请输入18位统一社会信用代码"
            onBlur={handleCreditCodeBlur}
            disabled={creditCodeChecking}
        />
      </Form.Item>

      <Form.Item name="short_name" label="公司简称" rules={[{ required: true, message: '请输入公司简称' }]}>
        <Input placeholder="请输入公司简称" />
      </Form.Item>
      <Form.Item name="legalPerson" label="法定代表人" rules={[{ required: true, message: '请输入法定代表人姓名' }]}>
        <Input placeholder="请输入法定代表人姓名" />
      </Form.Item>

      <Form.Item name="category" label="服务大类" rules={[{ required: true, message: '请选择服务大类' }]}>
        <Select
          placeholder="请选择服务大类"
          options={categoryOptions}
          loading={categoryLoading}
          showSearch
          optionFilterProp="label"
          onChange={() => form.setFieldsValue({ orgLabel: undefined })}
        />
      </Form.Item>

      <Form.Item name="orgLabel" label="专业类别" rules={[{ required: true, message: '请选择专业类别' }]}>
        <Select
          mode="multiple"
          placeholder={selectedCategory ? '请选择专业类别' : '请先选择服务大类'}
          options={orgLabelOptions}
          loading={orgLabelLoading}
          showSearch
          optionFilterProp="label"
          disabled={!selectedCategory}
        />
      </Form.Item>

      <Form.Item name="regTime" label="成立日期" rules={[{ required: true, message: '请选择成立日期' }]}>
        <DatePicker placeholder="请选择成立日期" style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name="regAmount" label="注册资金(万元)" rules={[{ required: true, message: '请输入注册资金' }]}>
        <InputNumber placeholder="请输入注册资金" style={{ width: '100%' }} min={0} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/,/g, '')} />
      </Form.Item>

      <Form.Item name="telephone" label="联系电话" rules={[{ required: true, message: '请输入机构联系电话' }]}>
        <Input placeholder="请输入机构联系电话" />
      </Form.Item>

      <Form.Item
            name="orgEmail"
            label="联系邮箱"
            rules={[
                { required: true, message: '请输入机构联系邮箱' },
                { type: 'email', message: '请输入正确的邮箱格式' },
            ]}
      >
        <Input placeholder="审核通过后账号密码将发送至此邮箱" />
      </Form.Item>

      <Form.Item name="checkUrl" label="公司网址" className={s.formItemFull}>
        <Input placeholder="请输入公司官网地址（选填）" />
      </Form.Item>

      <Form.Item label="所属区域" required className={`${s.formItemFull} ${s.areaFormItem}`}>
        <div className={s.areaGroup}>
          <Form.Item name="province" rules={[{ required: true, message: '请选择省份' }]} className={s.areaField}>
            <Select
              placeholder="请选择省份"
              options={provinceOptions}
              loading={provinceLoading}
              showSearch
              optionFilterProp="label"
              onChange={handleProvinceChange}
            />
          </Form.Item>
          <span className={s.areaConnector}>-</span>
          <Form.Item name="city" rules={[{ required: true, message: '请选择城市' }]} className={s.areaField}>
            <Select
              placeholder="请选择城市"
              options={cityOptions}
              loading={cityLoading}
              showSearch
              optionFilterProp="label"
              disabled={!selectedProvince}
              onChange={handleCityChange}
            />
          </Form.Item>
          <span className={s.areaConnector}>-</span>
          <Form.Item name="county" rules={[{ required: true, message: '请选择区/县' }]} className={s.areaField}>
            <Select
              placeholder="请选择区/县"
              options={countyOptions}
              loading={countyLoading}
              showSearch
              optionFilterProp="label"
              disabled={!selectedCity}
            />
          </Form.Item>
        </div>
      </Form.Item>

      <Form.Item name="regAddress" label="注册地址" rules={[{ required: true, message: '请输入注册地址' }]}>
        <Input placeholder="请输入公司注册地址" />
      </Form.Item>

      <Form.Item name="officeAddress" label="通信地址">
        <Input placeholder="请输入公司通信地址（选填）" />
      </Form.Item>

      <Form.Item name="busScope" label="业务范围" className={s.formItemFull}>
        <TextArea rows={2} placeholder="请输入机构业务范围（选填）" />
      </Form.Item>

      {/* <Form.Item name="checkDesc" label="机构简介" className={s.formItemFull}>
        <TextArea rows={2} placeholder="请输入机构简介（选填）" />
      </Form.Item> */}

      <Form.Item name="orgProfile" label="公司简介" className={s.formItemFull}>
        <div
          className={s.richTextTrigger}
          onClick={() => {
            const current = form.getFieldValue('orgProfile') || '';
            setRichTextModal({ visible: true, field: 'orgProfile', content: current });
          }}
        >
          <span className={form.getFieldValue('orgProfile') ? s.richTextTriggerEdited : s.richTextTriggerPlaceholder}>
            {form.getFieldValue('orgProfile') ? '已编辑，点击修改' : '点击编辑公司简介'}
          </span>
          <EditOutlined style={{ color: '#999' }} />
        </div>
      </Form.Item>
    </div>
  );

  // 第二步：上传资质证书、能力附表、营业执照
  const renderCertForm = () => (
    <div className={`${s.registerFormSection} ${s.registerFormGrid}`}>
      <Alert
        message="审核说明"
        description="请上传以下资质材料。后台审核通过后，您可以使用完整的系统功能。"
        type="info"
        showIcon
        className={s.formItemFull}
        style={{ marginBottom: 20 }}
      />

      <Form.Item
        name="businessCert"
        label="营业执照"
        rules={[{ required: true, message: '请上传营业执照' }]}
        valuePropName="fileList"
        getValueFromEvent={normalizeUploadEvent}
      >
        <Upload listType="text" maxCount={1} customRequest={handleUploadRequest} accept=".jpg,.jpeg,.png,.pdf">
          <Button icon={<UploadOutlined />}>上传营业执照</Button>
        </Upload>
      </Form.Item>

      <Form.Item
        name="checkAbility"
        label="资质认定证书附表"
        valuePropName="fileList"
        getValueFromEvent={normalizeUploadEvent}
      >
        <Upload listType="text" maxCount={1} customRequest={handleUploadRequest} accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx">
          <Button icon={<UploadOutlined />}>上传资质认定证书附表（选填）</Button>
        </Upload>
      </Form.Item>

      {/*<Form.Item*/}
      {/*  name="qualificationCert"*/}
      {/*  label="资质证书"*/}
      {/*  valuePropName="fileList"*/}
      {/*  getValueFromEvent={normalizeUploadEvent}*/}
      {/*>*/}
      {/*  <Upload listType="text" maxCount={5} customRequest={handleUploadRequest} accept=".jpg,.jpeg,.png,.pdf">*/}
      {/*    <Button icon={<UploadOutlined />}>上传资质证书（可多选，最多5份）</Button>*/}
      {/*  </Upload>*/}
      {/*</Form.Item>*/}

      <Form.Item name="manageOrgId" label="所属管理机关" rules={[{ required: true, message: '请选择所属管理机关' }]}>
        <Select
          placeholder="请选择所属管理机关"
          options={departOptions}
          loading={departLoading}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>

      <Form.Item
        name="checkLogo"
        label="公司logo"
        valuePropName="fileList"
        getValueFromEvent={normalizeUploadEvent}
      >
        <Upload listType="text" maxCount={1} customRequest={handleUploadRequest} accept=".jpg,.jpeg,.png,.pdf">
          <Button icon={<UploadOutlined />}>上传公司logo</Button>
        </Upload>
      </Form.Item>

      <Form.Item name="orgContact" label="注册联系人" rules={[{ required: true, message: '请输入注册联系人' }]}>
        <Input prefix={<UserOutlined />} placeholder="请输入机构入驻联系人姓名" />
      </Form.Item>
      <Form.Item
          name="contactTel"
          label="联系人电话"
          rules={[
            { required: true, message: '请输入联系人电话' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
          ]}
      >
        <Input prefix={<PhoneOutlined />} placeholder="请输入联系人手机号" />
      </Form.Item>
    </div>
  );

  // 第三步：设置登录密码
  const renderPasswordForm = () => (
    <div className={`${s.registerFormSection} ${s.registerFormGrid}`}>
      <Alert
        message="设置登录密码"
        description="请设置您的登录密码，您可以使用设置的账号和密码登录系统查看审核进度，后台审核通过后，您可以使用完整的系统功能。"
        type="info"
        showIcon
        className={s.formItemFull}
        style={{ marginBottom: 20 }}
      />

      <Form.Item
        name="regPassword"
        label="登录密码"
        rules={[
          { required: true, message: '请输入登录密码' },
          { min: 6, message: '密码至少6位' },
        ]}
        className={s.formItemFull}
      >
        <Input.Password placeholder="请输入登录密码（至少6位）" onChange={(e) => setRegPasswordValue(e.target.value)} />
      </Form.Item>

      <div className={`${s.strengthWrap} ${s.formItemFull}`}>
          <div className={s.strengthBar}>
            <span
              className={`${s.strengthSegment} ${regPasswordStrength.level >= 1 ? s.strengthActive : ''}`}
            />
            <span
              className={`${s.strengthSegment} ${regPasswordStrength.level >= 2 ? s.strengthActive : ''}`}
            />
            <span
              className={`${s.strengthSegment} ${regPasswordStrength.level >= 3 ? s.strengthActive : ''}`}
            />
          </div>
          <span className={s.strengthText}>
            {regPasswordStrength.level > 0 ? `密码强度：${regPasswordStrength.label}` : '密码强度'}
          </span>
        </div>

      <Form.Item
        name="confirmPassword"
        label="确认密码"
        dependencies={['regPassword']}
        className={s.formItemFull}
        rules={[
          { required: true, message: '请再次输入密码' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('regPassword') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('两次输入的密码不一致'));
            },
          }),
        ]}
      >
        <Input.Password placeholder="请再次输入登录密码" />
      </Form.Item>
    </div>
  );

  return (
    <Modal
      title={isSupplementMode ? '补充机构材料' : userType === 'organization' ? '机构注册' : '用户注册'}
      open={!!visible}
      onCancel={handleClose}
      footer={null}
      width={userType === 'organization' ? 1000 : 600}
      className={s.registerModal}
    >
      {/*{!isSupplementMode && (
        <div className={s.typeSelector}>
          <div
            className={`${s.typeSwitch} ${userType === 'organization' ? s.typeSwitchRight : s.typeSwitchLeft}`}
            role="tablist"
            aria-label="注册类型"
          >
            <button
              type="button"
              role="tab"
              aria-selected={userType === 'personal'}
              className={`${s.typeLabel} ${userType === 'personal' ? s.typeLabelActive : ''}`}
              onClick={() => changeUserType('personal')}
            >
              普通用户
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={userType === 'organization'}
              className={`${s.typeLabel} ${userType === 'organization' ? s.typeLabelActive : ''}`}
              onClick={() => changeUserType('organization')}
            >
              检测机构
            </button>
          </div>
        </div>
      )}*/}

      {userType === 'personal' ? (
        renderEnterpriseGuide()
      ) : (
        <>
          <Steps current={currentStep} className={s.registerSteps}>
            <Step title="基本信息" />
            <Step title="注册资料" />
            {!isSupplementMode && <Step title="设置密码" />}
          </Steps>

          <Form
            form={form}
            name="register"
            onFinish={handleRegister}
            layout="horizontal"
            labelAlign="left"
            labelCol={{ flex: '116px' }}
            wrapperCol={{ flex: '1 1 0' }}
            className={s.registerForm}
          >
            {currentStep === 0 && renderBasicInfoForm()}
            {currentStep === 1 && renderCertForm()}
            {currentStep === 2 && renderPasswordForm()}

            <div className={s.registerActions}>
              {currentStep === 0 && (
                <div className={s.stepButtons}>
                  <Button type="primary" onClick={nextStep} size="large" className={s.nextStepBtn}>
                    下一步
                  </Button>
                </div>
              )}
              {currentStep === 1 && (
                <div className={s.stepButtons}>
                  <Button onClick={() => setCurrentStep(0)} size="large">
                    上一步
                  </Button>
                  {isSupplementMode ? (
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size="large"
                      className={s.registerSubmitBtn}
                    >
                      提交补充材料
                    </Button>
                  ) : (
                    <Button type="primary" onClick={nextStep} size="large" className={s.nextStepBtn}>
                      下一步
                    </Button>
                  )}
                </div>
              )}
              {currentStep === 2 && (
                <div className={s.stepButtons}>
                  <Button onClick={() => setCurrentStep(1)} size="large">
                    上一步
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    className={s.registerSubmitBtn}
                  >
                    {isSupplementMode ? '提交补充材料' : '提交申请'}
                  </Button>
                </div>
              )}
            </div>
          </Form>
        </>
      )}

      {!isSupplementMode && (
        <div className={s.registerFooter}>
          <span>已有账号？</span>
          <Button type="link" onClick={() => { handleClose(); onSwitchToLogin?.(visible); }}>
            立即登录
          </Button>
        </div>
      )}

      <Modal
        title="编辑机构资料"
        open={richTextModal.visible}
        onCancel={() => setRichTextModal({ visible: false, field: '', content: '' })}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setRichTextModal({ visible: false, field: '', content: '' })}>
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={() => {
              form.setFieldValue(richTextModal.field, richTextModal.content);
              setRichTextModal({ visible: false, field: '', content: '' });
            }}
          >
            确定
          </Button>,
        ]}
        destroyOnClose
      >
        <WangEditor
          content={richTextModal.content}
          onChange={(html) => setRichTextModal((prev) => ({ ...prev, content: html }))}
          height="400px"
        />
      </Modal>
    </Modal>
  );
};

export default RegisterModal;
